// The invoice table tag, {{Patient.SelectedInvoice.WithPredefinedParameters(…)}}, edited in place. Its options
// spell out every section and column, so in the editor it was a paragraph of code nobody could change without
// rebuilding the table from scratch. Behind INVOICE_TABLE_TAG_EDIT_FEATURE, in an editor that writes templates
// (merge-tag values off), the tag is shown as a chip saying what the table prints, and clicking it opens the
// Define Invoice Parameters dialog filled from that tag, whose Update rewrites the tag where it sits.
//
// The chip is display only. chipTagsWithOptions (form-mode-editor-config.utils) wraps the tag as the editor reads
// content and unwraps it as it saves, so the stored HTML keeps the tag's exact text.
import type { Editor } from "tinymce";

import { neutral, primary } from "app/themes/colors";

import { filterConfigurationSections } from "./dialogs/utils";
import type { SelectSaleConfigurationValues } from "./dialogs/word-processor-select-sale-configuration-dialog.component";
import { TAG_WITH_OPTIONS_ATTR } from "./form-mode-editor-config.utils";
import { PATIENT_SELECTED_INVOICE_OPTIONS } from "./merge-tags/merge-tags-options/patient-selected-invoice.utils";
import { getConfigurableLineItemsHeaders } from "./merge-tags/merge-tags-values/common/configurable-merge-tags";
import {
  buildMergeTagOptionsString,
  parseMergeTagOptions
} from "./merge-tags/merge-tags.utils";

const TAG_NAME = PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS;
const TAG_START = `{{${TAG_NAME}(`;

export const INVOICE_TABLE_BARE_TAG = `{{${TAG_NAME}}}`;

/** An invoice table tag that carries its options (the bare menu tag has none). */
export const isInvoiceTableTag = (text: string | null | undefined) => {
  const tag = (text ?? "").trim();
  return tag.startsWith(TAG_START) && tag.endsWith(")}}");
};

export const invoiceTableTagOptions = (tag: string): Record<string, any> =>
  parseMergeTagOptions(tag.trim()).options;

/** The tag for a set of options, written the way the dialog has always written it. */
export const invoiceTableTag = (options: Record<string, any>) =>
  `{{${TAG_NAME}${buildMergeTagOptionsString(options)}}}`;

const SECTION_NAMES: Array<[string, string]> = [
  ["include_hearing_aids", "Hearing aids"],
  ["include_domes", "Domes"],
  ["include_receivers", "Receivers"],
  ["include_tubes", "Tubes"],
  ["include_earmolds", "Earmolds"],
  ["include_chargers", "Chargers"],
  ["include_batteries", "Batteries"],
  ["include_remotes", "Remotes"],
  ["include_others", "Other accessories"],
  ["include_services_dispensing_fees", "Services & fees"],
  ["include_totals", "Totals"]
];

/** What the table prints, in words: "Invoice table · Hearing aids, Totals · 9 columns". */
export const describeInvoiceTableTag = (options: Record<string, any>) => {
  const sections = SECTION_NAMES.filter(([key]) => options[key]).map(
    ([, name]) => name
  );
  // Counted as the dialog counts them: a section the tag leaves out prints no columns, whatever the tag lists.
  const columns = getConfigurableLineItemsHeaders(
    filterConfigurationSections(options as SelectSaleConfigurationValues)
  ).length;
  return [
    "Invoice table",
    sections.length ? sections.join(", ") : "No sections chosen",
    `${columns} column${columns === 1 ? "" : "s"}`
  ].join(" · ");
};

const CHIP = `span[${TAG_WITH_OPTIONS_ATTR}]`;
// How far the pointer may move between press and release for it to still count as a click on the chip.
const CLICK_SLOP_PX = 4;
// Display only: registered as a temporary attribute, so it is never saved and never an undo step.
const LABEL = "data-mce-invoice-table-label";

// TinyMCE's own copy of a selected non-editable element, kept off screen in a [data-mce-bogus] container while the
// chip is selected. It carries the chip's text, so it is never a chip: counted as one, it was where the caret went
// after an Update (the real chip had its new text by then), and the editor scrolled away from the table.
const isTinyMceCopy = (element: Element) =>
  Boolean(element.closest("[data-mce-bogus]"));

/** The invoice table chips on the page, in document order. */
const chipsIn = (editor: Editor) =>
  Array.from(
    editor.getBody()?.querySelectorAll<HTMLElement>(CHIP) ?? []
  ).filter(
    (chip) => !isTinyMceCopy(chip) && isInvoiceTableTag(chip.textContent)
  );

// The code is hidden at font-size 0 and the chip reads its description. Sized in px, not from the text around it:
// the table prints in its own size whatever the paragraph's, and the description must stay readable.
const CHIP_STYLE = `
.mce-content-body ${CHIP}[${LABEL}] {
  font-size: 0 !important;
  display: inline-block;
  box-sizing: border-box;
  max-width: 100%;
  padding: 6px 12px;
  border: 1px solid ${primary[300]};
  border-radius: 6px;
  background: ${neutral[50]};
  line-height: 0;
  cursor: pointer !important;
}
.mce-content-body ${CHIP}[${LABEL}]:hover { background: ${neutral[100]}; }
.mce-content-body ${CHIP}[${LABEL}]::before {
  content: attr(${LABEL});
  font: 700 16px/1.4 Lato, LatoRegular, sans-serif;
  color: ${primary[600]};
}
.mce-content-body ${CHIP}[${LABEL}]::after {
  content: "Click to edit";
  margin-left: 12px;
  font: 400 14px/1.4 Lato, LatoRegular, sans-serif;
  color: ${primary[400]};
  text-decoration: underline;
}`;

export type InvoiceTableTagTarget = {
  /** The chip as it was clicked. */
  chip: HTMLElement;
  /** The tag's text when it was clicked. */
  tag: string;
  /** Its place among the editor's invoice table chips (0 for the first), for finding it again. */
  index?: number;
};

/**
 * The chip a target names: the one clicked while it is still on the page, else the chip in the same place. The
 * element is often gone: tinymce-react sets the content again on the first click after a template loads, before
 * the dialog has even opened. A reset re-reads the same document, so the place still names the same table, and
 * the chip there must hold one of `tags` (its text when clicked, or the text an Update gave it).
 */
const resolveChip = (
  editor: Editor,
  { chip, tag, index }: InvoiceTableTagTarget,
  tags: string[] = [tag]
) => {
  if (chip.isConnected && editor.getBody()?.contains(chip)) return chip;
  const texts = new Set(tags.map((each) => each.trim()));
  const holds = (each: HTMLElement) =>
    texts.has(each.textContent?.trim() ?? "");
  const chips = chipsIn(editor);
  const atPlace = index === undefined ? undefined : chips[index];
  return atPlace && holds(atPlace) ? atPlace : chips.find(holds);
};

// ENG-2447: the editor's value is read from its own change events; a change made outside typing must say so.
const commit =
  (editor: Editor, change: (chip: HTMLElement) => void) =>
  (chip: HTMLElement) => {
    editor.undoManager.transact(() => change(chip));
    editor.nodeChanged();
    editor.dispatch("change");
  };

/** Rewrites the tag in place. False when it is no longer in the editor. */
export const replaceInvoiceTableTag = (
  editor: Editor,
  target: InvoiceTableTagTarget,
  tag: string
) => {
  const chip = resolveChip(editor, target);
  if (!chip) return false;
  commit(editor, (each) => {
    each.textContent = tag;
  })(chip);
  return true;
};

/** Takes the tag out of the editor (undo puts it back). False when it is no longer there. */
export const removeInvoiceTableTag = (
  editor: Editor,
  target: InvoiceTableTagTarget
) => {
  const chip = resolveChip(editor, target);
  if (!chip) return false;
  commit(editor, (each) => editor.dom.remove(each))(chip);
  return true;
};

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

/**
 * The chip just before a collapsed caret, if any: the caret sits in TinyMCE's caret holder after it (a text node of
 * U+FEFF), or in its parent right after it.
 */
const chipBeforeCaret = (editor: Editor): HTMLElement | null => {
  const range = editor.selection.getRng();
  if (!range.collapsed) return null;
  const { startContainer: node, startOffset: offset } = range;
  const before =
    node.nodeType === TEXT_NODE
      ? /^\uFEFF*$/.test((node as Text).data.slice(0, offset))
        ? node.previousSibling
        : null
      : (node.childNodes[offset - 1] ?? null);
  return before?.nodeType === ELEMENT_NODE &&
    (before as Element).matches(CHIP) &&
    isInvoiceTableTag(before.textContent)
    ? (before as HTMLElement)
    : null;
};

/**
 * Puts the caret just after the tag, once its dialog has closed, and scrolls the table into view: the user stays
 * on the table they edited. Left selected, the next Enter would open the dialog again and the next keystroke would
 * type over the table. `tag` is the tag's text now, when an Update changed it.
 */
export const placeCaretAfterInvoiceTableTag = (
  editor: Editor,
  target: InvoiceTableTagTarget,
  tag: string = target.tag
) => {
  const chip = resolveChip(editor, target, [tag, target.tag]);
  if (!chip) return;
  editor.selection.select(chip);
  editor.selection.collapse(false);
  chip.scrollIntoView?.({ block: "nearest" });
};

/**
 * Opens an invoice table tag for editing on a click (press and release on it), or on Enter while the chip is selected. With `showLabels`, the
 * chip also says what the table prints; a Form leaves that off, because the Digital Form editor names its chips.
 * Called from the editor's setup.
 */
export const attachInvoiceTableTagEditing = (
  editor: Editor,
  {
    isEnabled,
    onEdit,
    showLabels,
    keepCaretAfterChips = showLabels
  }: {
    isEnabled: () => boolean;
    onEdit: (target: InvoiceTableTagTarget) => void;
    showLabels: boolean;
    keepCaretAfterChips?: boolean;
  }
) => {
  const edit = (found: Element | null | undefined) => {
    // On Enter, the selected node can be TinyMCE's off-screen copy; the real chip is the one marked selected.
    const chip =
      found && isTinyMceCopy(found)
        ? editor.getBody()?.querySelector(`${CHIP}[data-mce-selected]`)
        : found;
    if (!chip || !isEnabled() || !isInvoiceTableTag(chip.textContent)) {
      return false;
    }
    onEdit({
      chip: chip as HTMLElement,
      tag: chip.textContent?.trim() ?? "",
      index: Math.max(chipsIn(editor).indexOf(chip as HTMLElement), 0)
    });
    return true;
  };

  // A press and release on the chip, not "click". Pressing on an unfocused editor shows its toolbar, which pushes
  // the content down under a still pointer: the mouseup then lands on another element and the browser fires the
  // click on the body, so the first click did nothing. Measured in screen coordinates, which that shift does not
  // change; a release more than a few px away is a drag (selecting text), not a click.
  let press: { chip: Element; x: number; y: number } | null = null;
  editor.on("mousedown", (event: MouseEvent) => {
    const chip =
      event.button === 0
        ? (event.target as Element | null)?.closest?.(CHIP)
        : null;
    press = chip ? { chip, x: event.screenX, y: event.screenY } : null;
  });
  editor.on("mouseup", (event: MouseEvent) => {
    const pressed = press;
    press = null;
    if (
      !pressed ||
      event.button !== 0 ||
      Math.abs(event.screenX - pressed.x) > CLICK_SLOP_PX ||
      Math.abs(event.screenY - pressed.y) > CLICK_SLOP_PX
    ) {
      return;
    }
    edit(pressed.chip);
  });
  editor.on("keydown", (event: KeyboardEvent) => {
    // Plain Enter only: Shift+Enter and the rest keep their line-break meaning.
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.ctrlKey ||
      event.altKey ||
      event.metaKey
    ) {
      return;
    }
    if (edit(editor.selection.getNode()?.closest?.(CHIP))) {
      event.preventDefault();
    }
  });

  // tinymce-react sets the content again whenever the value it is handed differs from the editor's own, which it
  // does on the first re-render after a template loads (the stored HTML is not the editor's normalized copy). It
  // keeps the caret with a path bookmark, and a caret after a chip that ends its line is in TinyMCE's temporary
  // caret holder, which the new content lacks, so the caret fell to the start of the document and text typed after
  // the table landed at the top. Put it back after the same chip; tinymce-react's own restore then finds nothing
  // to move to and leaves it there.
  if (keepCaretAfterChips) {
    let caretAfter = -1;
    editor.on("BeforeSetContent", () => {
      const chip =
        isEnabled() && editor.hasFocus() ? chipBeforeCaret(editor) : null;
      caretAfter = chip ? chipsIn(editor).indexOf(chip) : -1;
    });
    editor.on("SetContent", () => {
      const chip = caretAfter >= 0 ? chipsIn(editor)[caretAfter] : undefined;
      caretAfter = -1;
      if (!chip) return;
      editor.selection.select(chip);
      editor.selection.collapse(false);
    });
  }

  if (!showLabels) return;

  // Runs on every caret move (NodeChange), so each tag's label is worked out once and remembered by its text.
  const labels = new Map<string, string>();
  const labelFor = (tag: string) => {
    let label = labels.get(tag);
    if (label === undefined) {
      label = describeInvoiceTableTag(invoiceTableTagOptions(tag));
      labels.set(tag, label);
    }
    return label;
  };

  const relabel = () => {
    if (!isEnabled()) return;
    chipsIn(editor).forEach((chip) => {
      const label = labelFor(chip.textContent ?? "");
      if (chip.getAttribute(LABEL) !== label) chip.setAttribute(LABEL, label);
    });
  };

  editor.on("init", () => {
    editor.dom.addStyle(CHIP_STYLE);
    editor.serializer.addTempAttr(LABEL);
    relabel();
  });
  editor.on("SetContent NodeChange Undo Redo", relabel);
};
